import React, { useState, useEffect } from 'react';
import moment from 'moment';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

import PaginationMui from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';

import { PaginationItem } from '@mui/material';

import { useGlobal } from '../../contexts/Global/context';

import Icon from '../Icons';
import Alert from '../Alert';
import Loading from '../Loading';

import { TableContent } from '../../styles/styles';
import { LinkButton } from '../../styles/global';
import * as S from './styles';

function SortIcon() {
    const { contrastTheme } = useGlobal();

    return (
        <S.AlignIconSort>
            <Icon name="arrow_drop_up" style={{ color: contrastTheme ? '#FFF333' : '#F49E00' }} />
            <Icon name="arrow_drop_down" style={{ marginTop: '-15px', color: contrastTheme ? '#FFF333' : '#F49E00' }} />
        </S.AlignIconSort>
    );
}

function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (moment(valueA).isValid()) {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
    }

    if (!valueB || !valueA) {
        return -1;
    }

    if (valueB < valueA) {
        return -1;
    }

    if (valueB > valueA) {
        return 1;
    }

    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el) => el);

    stabilizedThis.sort((a, b) => {
        const order = comparator(a, b);
        if (order !== 0) {
            return order;
        }
        return a - b;
    });

    return stabilizedThis;
}

function getButtonVisibility(props) {
    if (props.type === 'previous' && props.page === 0) {
        return 'hidden';
    }

    if (props.type === 'next' && props.page - 1 === props.qtyPages) {
        return 'hidden';
    }
    return 'visible';
}

function PaginationRounded({ onChange, qtyPages, siblingCount }) {
    const { contrastTheme } = useGlobal();

    return (
        <Stack spacing={2}>
            <PaginationMui
                renderItem={(props) => {
                    props.style = {
                        visibility: getButtonVisibility({ ...props, qtyPages }),
                    };

                    if (props.type === 'previous') {
                        return (
                            <LinkButton id="previous" {...props}>
                                <Icon name="ArrowLeft" color={contrastTheme ? '#FFF333' : '#EC6E0C'} />
                                <S.PaginationText>Anterior</S.PaginationText>
                            </LinkButton>
                        );
                    }

                    if (props.type === 'next') {
                        return (
                            <LinkButton id="forward" {...props}>
                                <S.PaginationText>Próxima</S.PaginationText>
                                <Icon name="ArrowRight" color={contrastTheme ? '#FFF333' : '#EC6E0C'} />
                            </LinkButton>
                        );
                    }

                    if (props.type.includes('ellipsis')) {
                        delete props.onClick;
                    }

                    return <PaginationItem {...props} />;
                }}
                count={qtyPages}
                onChange={onChange}
                variant="outlined"
                shape="rounded"
                siblingCount={siblingCount}
            />
        </Stack>
    );
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, columns } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <S.CustomTableHead>
            <TableRow>
                {columns.map(({ key, sortable, name }) => (
                    <React.Fragment key={key}>
                        <TableCell
                            component="th"
                            align={sortable ? 'right' : 'left'}
                            sortDirection={orderBy === key ? order : false}
                        >
                            <TableSortLabel
                                active={orderBy === key}
                                direction={orderBy === key ? order : 'asc'}
                                onClick={createSortHandler(key)}
                                IconComponent={sortable ? SortIcon : null}
                            >
                                {name}
                            </TableSortLabel>
                        </TableCell>
                    </React.Fragment>
                ))}
            </TableRow>
        </S.CustomTableHead>
    );
}

export default function DataTableMui({
    data,
    loading,
    alreadySearched,
    columns,
    rowMutation,
    firstOrderBy,
    emptyMessage,
    clickableRow,
}) {
    const { contrastTheme } = useGlobal();

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(firstOrderBy);
    const [pagination, setPagination] = useState({ activePage: 0, showingData: [] });

    const range = 2;
    const rowsPerPage = 10;
    const qtyPages = Math.ceil(data.length / rowsPerPage) || 1;

    const columnsToShow = columns.filter((column) => !column.hideColumn);

    function handleChangePage(activePage) {
        if (activePage < qtyPages) {
            setPagination({
                activePage,
                showingData: [...stableSort(data, getComparator(order, orderBy))].splice(
                    activePage * rowsPerPage,
                    rowsPerPage
                ),
            });
        }
    }

    const handleRequestSort = (_, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    useEffect(() => {
        setPagination({
            activePage: 0,
            showingData: [...stableSort(data, getComparator(order, orderBy))].splice(0, rowsPerPage),
        });
    }, [data]);

    useEffect(() => {
        setPagination({
            activePage: pagination.activePage,
            showingData: [...stableSort(data, getComparator(order, orderBy))].splice(0, rowsPerPage),
        });
    }, [orderBy, order]);

    const renderRowContent = (row, column) => {
        if (column.mutation) {
            return column.mutation(column.selector(row));
        }

        return column.selector(row);
    };

    if (loading) {
        return (
            <TableContent>
                <Loading />
            </TableContent>
        );
    }

    if (!alreadySearched) {
        return <></>;
    }

    if (!loading && alreadySearched && pagination.showingData.length === 0) {
        return <Alert type="2" title={emptyMessage || 'Dados não encontrados'} />;
    }

    return (
        <>
            <S.CustomTableContainer contrastTheme={contrastTheme}>
                <Table aria-labelledby="tableTitle">
                    <EnhancedTableHead
                        order={order}
                        columns={columnsToShow}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                    />

                    <TableBody>
                        {pagination.showingData.map((row, index) => {
                            const labelId = `enhanced-table-checkbox-${index}`;

                            return (
                                <S.CustomTableRow
                                    hover
                                    contrastTheme={contrastTheme}
                                    clickableRowS={clickableRow || false}
                                    onClick={() => (rowMutation?.hasClick(row) ? rowMutation.click(row) : null)}
                                    tabIndex={-1}
                                    key={index}
                                    className={rowMutation?.class ? rowMutation.class(row) : ''}
                                >
                                    {columnsToShow.map((column) => (
                                        <S.CustomTableCell
                                            component="td"
                                            id={labelId}
                                            scope="row"
                                            style={
                                                column.customStyles &&
                                                typeof column.customStyles === 'function' &&
                                                column.customStyles(row)
                                            }
                                            className={column.customClassName ? column.customClassName : null}
                                        >
                                            {renderRowContent(row, column)}
                                        </S.CustomTableCell>
                                    ))}
                                </S.CustomTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </S.CustomTableContainer>
            <S.PaginationContainer className="pagination-align">
                <PaginationRounded
                    onChange={(_, value) => handleChangePage(value - 1)}
                    qtyPages={qtyPages}
                    siblingCount={range}
                />
            </S.PaginationContainer>
        </>
    );
}
